import moment from 'moment';
import { currencySymbols } from '../Shell/PandoraComponents/core/model/enumerations';
import { utils } from '../Utils/mx-utils';

const devLanguageName = 'en';
const defaultDateFormat = 'L'; // moment locale date format, DD/MM/YYY for GB
const shortTimeAgoSettings = {
	future: '%s',
	past: '-%s',
	s: 'seconds',
	m: '1min',
	mm: '%dmin',
	h: '1h',
	hh: '%dh',
	d: '1d',
	dd: '%dd',
	M: '1m',
	MM: '%dm',
	y: '1y',
	yy: '%dy',
};

export class I18n {
	static $inject = ['$filter', '$locale'];

	constructor($filter, $locale) {
		this.$filter = $filter;
		this.$locale = $locale;

		this._speedingUpLocalCache = {};

		this.language = devLanguageName;
	}

	get localization() {
		return this._localization;
	}
	set localization(value) {
		this._localization = value;
	}

	get language() {
		return this._language;
	}
	set language(value) {
		this._language = value;
		this._defaultLocaleData = moment.localeData(this._language);
	}

	formatTimeDuration(minutes, fullFormat) {
		let formattedDuration = '';
		const duration = moment.duration(parseFloat(minutes) || 0, 'minutes');
		const s = duration.seconds();
		const m = duration.minutes();
		const h = duration.hours();
		const d = Math.floor(duration.asDays());
		const i18n = {
			days: ' ' + this.get('components.common.days'),
			hours: ' ' + this.get('components.common.hours'),
			minutes: ' ' + this.get('components.common.minutes'),
			seconds: ' ' + this.get('components.common.seconds'),
			and: ' ' + this.get('components.common.and') + ' ',
		};
		if (d) {
			// minutes and seconds don't matter
			formattedDuration = fullFormat ? d + i18n.days + i18n.and + h + i18n.hours : d + 'd : ' + h + 'h';
		} else if (h) {
			// seconds don't matter
			formattedDuration = fullFormat ? h + i18n.hours + i18n.and + m + i18n.minutes : h + 'h : ' + m + 'm';
		} else {
			formattedDuration = m + 'm : ' + s + 's';
			formattedDuration = fullFormat ? h + i18n.minutes + i18n.and + m + i18n.seconds : m + 'm : ' + s + 's';
		}
		return formattedDuration;
	}

	formatCurrency(value, currencyCode, useSymbols, availableCurrencies) {
		if (value !== undefined && value !== null) {
			let val = value;

			if (val.Value || val.CC) {
				val = val.Value || 0;
				if (!currencyCode && value.CC) {
					currencyCode = value.CC;
				}
			}
			if (useSymbols && currencyCode) {
				currencyCode = this.getSymbolByCode(availableCurrencies, currencyCode) || currencySymbols[currencyCode] || currencyCode;
			}

			return this.$filter('currency')(val, currencyCode && currencyCode !== '' ? currencyCode + '\xa0' : '');
		}

		return null;
	}

	getSymbolByCode(availableCurrencies, code) {
		if (availableCurrencies) {
			const item = availableCurrencies.find((currency) => currency.code === code);

			if (item) {
				return item.symbol;
			}
		}

		return null;
	}

	formatNumber(value, type, format) {
		if (typeof value !== 'undefined' && value !== null && type) {
			format = format || this.getNumberFormat(type);
			const localeFormats = angular.copy(this.getLocale().NUMBER_FORMATS);
			const targetFormats = { PATTERNS: [{}, {}] };
			let precision;

			if (format.indexOf(',') === -1) {
				targetFormats.GROUP_SEP = '';
			}

			if (type === mx.shell.ObjectPropertyType.Float.name || type === mx.shell.ObjectPropertyType.Decimal.name || Math.floor(value) !== value) {
				if (format.indexOf('.') !== -1) {
					precision = format.split('.')[1];
					if (precision) {
						if (precision.indexOf('[') !== -1) {
							precision = precision.replace(']', '').split('[');

							targetFormats.PATTERNS[0].minFrac = precision[0].length;
							targetFormats.PATTERNS[0].maxFrac = precision[0].length + precision[1].length;
						} else {
							targetFormats.PATTERNS[0].minFrac = targetFormats.PATTERNS[0].maxFrac = precision.length;
						}
					}
				} else {
					targetFormats.DECIMAL_SEP = '';
				}
			}

			angular.merge(this.getLocale().NUMBER_FORMATS, targetFormats);
			const output = this.$filter('number')(value);
			angular.merge(this.getLocale().NUMBER_FORMATS, localeFormats);

			return output;
		}

		return null;
	}

	formatDate(value, format) {
		if (!value) {
			return '';
		}
		if (typeof value === 'string') {
			value = new Date(value);
		}
		const m = moment(value);
		if (!m.isValid()) {
			return '';
		}

		format = format || defaultDateFormat;

		switch (format) {
			case 'timeAgo':
				return this.formatTimeAgo(value, this._defaultLocaleData._relativeTime);
			case 'timeAgoShort':
				return this.formatTimeAgo(value, shortTimeAgoSettings);
			default:
				return moment(value).format(this.angularToMomentDateFormat(format)).toString();
		}
	}

	parseDate(dateString, format) {
		format = this.angularToMomentDateFormat(format || defaultDateFormat);
		dateString = mx.components.Utils.convertDateFrom6or8Digits(dateString);
		const m = moment(dateString, format, true);
		return m.isValid() ? m.toDate() : new Date(NaN);
	}

	getMomentFormat(format) {
		return moment.localeData(this.$locale.id ?? 'en').longDateFormat(moment(new Date(), format).creationData().format);
	}

	angularToMomentDateFormat(dateTimeFormat) {
		const mediumDateFormat = this.getMomentFormat('LL').replace('MMMM', 'MMM');
		const shortDateFormat = this.getMomentFormat('L');
		switch (dateTimeFormat) {
			case 'medium':
				return mediumDateFormat + ' ' + this.getMomentFormat('LTS');
			case 'short':
				return shortDateFormat + ' ' + this.getMomentFormat('LT');
			case 'longDate':
				return 'LL';
			case 'fullDate':
				return 'LLLL'; //extra time
			case 'mediumDate':
				return mediumDateFormat;
			case 'shortDate':
				return shortDateFormat;
			case 'mediumTime':
				return 'LTS';
			case 'shortTime':
				return 'LT';

			case 'timeAgo':
				return 'L';
			case 'timeAgoShort':
				return 'L';
			default:
				return dateTimeFormat;
		}
	}

	getDateFormatString(dateFormat) {
		return this.getMomentFormat(this.angularToMomentDateFormat(dateFormat || defaultDateFormat));
	}

	formatUTCDateForAria(value, format) {
		const ariaFormat =
			(['timeAgo', 'timeAgoShort'].includes(format) && 'timeAgo') ||
			(['medium', 'short'].includes(format) && 'LLL') ||
			(['mediumDate', 'shortDate'].includes(format) && 'LL') ||
			format;
		return this.formatUTCDate(value, ariaFormat);
	}

	formatUTCDate(value, format) {
		return this.formatDate(new Date(value), format);
	}

	formatTimeAgo(value, settings) {
		moment.updateLocale(utils.getMomentJsSupportedLocale(this.language), { relativeTime: settings });
		return moment(value).fromNow(false);
	}

	getNumberFormat(type) {
		switch (type) {
			case mx.shell.ObjectPropertyType.Float.name:
			case mx.shell.ObjectPropertyType.Decimal.name:
				return '#,#.##[####]';

			case mx.shell.ObjectPropertyType.Long.name:
			case mx.shell.ObjectPropertyType.Integer.name:
				return '#';

			default:
				return;
		}
	}

	getLocale() {
		return this.$locale;
	}

	get(key) {
		if (!key) {
			throw new Error('No localization key provided');
		}
		if (typeof this._speedingUpLocalCache[key] !== 'undefined') {
			return this._speedingUpLocalCache[key];
		}
		const keyChain = key.split('.');
		const res = this.localize(keyChain) || '';
		if (!res) {
			console.error(`MX-i18n for "${key}" is not provided in DB`);
		}
		this._speedingUpLocalCache[key] = res;
		return res;
	}

	localize(keyChunks) {
		if (this.localization) {
			let localization = this.localization;
			keyChunks.forEach((chunk, index) => {
				localization = localization[chunk] || (index + 1 === keyChunks.length ? null : {});
			});
			return localization;
		}
	}

	notAvailable() {
		return this.get('components.common.notAvailable');
	}

	isEmpty(value) {
		return !value || value.toUpperCase() === this.notAvailable();
	}

	isNotEmpty(value) {
		return !this.isEmpty(value);
	}

	getFirstNotEmpty(arr, fallback) {
		let result = fallback;
		if (arr && arr.length) {
			arr.some((value) => {
				if (value && this.isNotEmpty(value)) {
					result = value;
					return true;
				}
			});
		}
		return result || this.notAvailable();
	}

	getFormatted(key, ...params) {
		let str = this.get(key);
		if (!str) {
			return str;
		}
		str = this.replaceParams(str, typeof params?.[0] === 'object' ? params[0] : params);
		return str;
	}

	getNode(key) {
		const res = this.get(key);
		if (res && typeof res === 'object') {
			res.$format = (subKey, ...params) => this.getFormatted(key + '.' + subKey, params);
			return res;
		}
		return {};
	}

	replaceParams(str, params) {
		if (Array.isArray(params)) {
			params.forEach((param, index) => (str = this.replaceParam(str, index, param)));
		} else if (typeof params === 'object' && params) {
			Object.entries(params).forEach(([key, value]) => (str = this.replaceParam(str, key, value)));
		}

		return str;
	}

	replaceParam(str, key, value) {
		const regEx = new RegExp('\\{' + key + '\\}', 'gm');
		return str.replace(regEx, value);
	}
}
